/* eslint-disable camelcase */
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { FormControl, Grid, IconButton, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { SettingsIcon, TinyArrowIcon } from "assets/icons";
import { DETECTOR_GROUP_ALL_OPTION } from "components/detectorManagement/FilterDetector/DetectorGroupAutocomplete/constants";
import { DetectorGroupAutocomplete } from "components/detectorManagement/FilterDetector/DetectorGroupAutocomplete/DetectorGroupAutocomplete";
import { useAppSelector } from "hooks/store";
import { GroundlightButton } from "new-components/buttons/GroundlightButton";
import { NewDetectorButton } from "new-components/buttons/NewDetectorButton";
import { FilterDetectorState } from "pages/detectorManagement/DetectorManagement";
import { getUserState } from "store/slices/Auth/authSlice";
import { User } from "types/users";

interface FilterDetectorProps {
  filterState: Partial<FilterDetectorState>;
  handleChangeFilterState: (state: Partial<FilterDetectorState>) => void;
  numDetectors: number;
}

const filterOptions = [
  {
    name: "Most recently used",
    value: "recent_used",
  },
  {
    name: "Creation date",
    value: "created_at",
  },
  // TODO: improve backend
  // {
  //   name: "Most Flagged",
  //   value: "most_flagged",
  // },
];

interface SearchAdornmentProps {
  existText: boolean;
  handleChangeSearchText: (value: string) => void;
  textValue: string;
}

function SearchAdornment(props: SearchAdornmentProps) {
  const { existText, handleChangeSearchText, textValue } = props;
  return (
    <>
      {existText && (
        <IconButton onClick={() => handleChangeSearchText("")}>
          <CloseIcon sx={{ opacity: "50%" }} />
        </IconButton>
      )}
      <IconButton onClick={() => handleChangeSearchText(textValue)}>
        <SearchIcon sx={{ transform: "rotate(90deg)", opacity: "50%" }} />
      </IconButton>
    </>
  );
}

export function FilterDetector(props: FilterDetectorProps) {
  const { filterState, handleChangeFilterState, numDetectors } = props;

  const [textValue, setTextValue] = useState<string>(filterState.search || "");
  const userState: User = useAppSelector(getUserState);

  const filterModeOptions = [
    {
      name: "Binary",
      value: "BINARY",
    },
    userState.enable_count_mode
      ? {
          name: "Count",
          value: "COUNT",
        }
      : {},
    userState.enable_multi_class_mode
      ? {
          name: "Multiple Choice",
          value: "MULTI_CLASS",
        }
      : {},
    userState.enable_text_mode
      ? {
          name: "Text Recognition",
        value: "TEXT",
      }
      : {},
    userState.enable_bounding_box_mode
      ? {
        name: "Bounding Box",
        value: "BOUNDING_BOX",
      }
      : {},
  ];

  const existText = textValue.trim().length > 0;

  function executeSearch(event: any) {
    if (event.key === "Enter") {
      handleChangeFilterState({
        search: textValue,
      });
    }
  }

  const navigate = useNavigate();

  return (
    <Grid container columns={18} spacing={1} alignItems="center" sx={{ px: "20px", my: "5px" }}>
      <Grid item xs={18} sm={8} md={3}>
        <TextField
          fullWidth
          variant="outlined"
          defaultValue={filterState.search}
          placeholder="Search by name"
          aria-label="name-input"
          onChange={(e) => setTextValue(e.target.value)}
          onKeyUp={(event: any) => {
            if (event.key === "Enter") executeSearch(event);
          }}
          InputProps={{
            endAdornment: (
              <SearchAdornment
                existText={existText}
                handleChangeSearchText={(value) =>
                  handleChangeFilterState({
                    search: value,
                  })
                }
                textValue={textValue}
              />
            ),
            sx: {
              ".MuiInputBase-input": { padding: 0 },
              paddingLeft: 1,
              paddingRight: 0.5,
            },
          }}
        />
      </Grid>
      <Grid item xs={9} sm={4.5} md={3}>
        <FormControl size="small" fullWidth>
          <InputLabel id="demo-simple-select-label">Sort</InputLabel>
          <Select
            id="demo-simple-answer-select"
            value={filterState.ordering}
            onChange={(e) =>
              handleChangeFilterState({
                ordering: e.target.value,
              })
            }
            fullWidth
            displayEmpty
            label="Sort"
            startAdornment={
              <IconButton
                onClick={() =>
                  handleChangeFilterState({
                    sort: filterState.sort === "" ? "-" : "",
                  })
                }
                className="opacity-[50%]"
                sx={{ transform: filterState.sort === "" ? "rotate(180deg)" : "" }}
                data-testid="btn-sort"
              >
                <TinyArrowIcon color="#49494d" />
              </IconButton>
            }
            className="pl-1"
            sx={{
              ".MuiSelect-outlined": { paddingX: "2px" },
            }}
          >
            {filterOptions.map((option: any) => (
              <MenuItem value={option.value} key={option.value} data-testid="btn-option">
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={9} sm={4.5} md={3}>
        <FormControl size="small" fullWidth>
          <InputLabel id="simple-mode-select-label">Mode</InputLabel>
          <Select
            id="simple-mode-select"
            value={filterState.mode === "" ? "all" : filterState.mode}
            onChange={(e) =>
              handleChangeFilterState({
                mode: e.target.value,
              })
            }
            fullWidth
            displayEmpty
            label="Mode"
            className="pl-1"
            sx={{
              ".MuiSelect-outlined": { paddingX: "2px" },
            }}
          >
            <MenuItem value="all">All</MenuItem>
            {filterModeOptions &&
              filterModeOptions.map((option) =>
                option.value ? (
                  <MenuItem value={option.value} key={option.value}>
                    {option.name}
                  </MenuItem>
                ) : null
              )}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={9} sm={4.5} md={3}>
        <DetectorGroupAutocomplete
          data-testid="groupId"
          hasAllOption
          value={
            filterState.group_id && filterState.group_name
              ? {
                  label: filterState.group_name,
                  value: filterState.group_id,
                }
              : undefined
          }
          onChange={(option) => {
            const isAllOption = option === DETECTOR_GROUP_ALL_OPTION;

            if (isAllOption) {
              handleChangeFilterState({
                group_name: undefined,
                group_id: undefined,
              });
              return;
            }

            handleChangeFilterState({
              group_name: option?.label,
              group_id: option?.value,
            });
          }}
        />
      </Grid>
      <Grid item xs={9} sm={4.5} md={3}>
        <GroundlightButton
          variant="border"
          fullWidth
          onClick={() => navigate("/reef/detectors/groups")}
          endIcon={SettingsIcon}
          sx={{
            whiteSpace: "nowrap",
          }}
        >
          Manage groups
        </GroundlightButton>
      </Grid>
      <Grid item xs={9} sm={4.5} md={3}>
        <NewDetectorButton numDetectors={numDetectors} />
      </Grid>
    </Grid>
  );
}
